/**
 * Works out when a page has finished loading, so a print macro can capture it.
 *
 * There is no single "loaded" signal in the app: pages fetch through the api
 * module, some fetch again in watchers once the first fetch lands, child
 * components load their own data one after another (Platings loads a meal at a
 * time) and a few calculate for a while after the data arrives. So a page counts
 * as settled when, for a quiet period, all of these hold:
 *   - no api request is in flight
 *   - no mounted vue component has a truthy loading flag in its data
 *     (loading, isLoading, isReloading, loadingOrders, loadingTasks, ...)
 *   - nothing on the page shows a loading indicator (vuetify progress bars,
 *     spinners, skeleton loaders, loading buttons, or any element marking
 *     itself busy with data-loading="true")
 *   - no visible "please wait" / "loading..." message
 */

// vuetify 2 markers for something still loading, plus an opt-in attribute a page can set
export const LOADING_SELECTORS = [
  '.v-progress-circular--indeterminate',
  '.v-progress-linear--active',
  '.v-skeleton-loader--is-loading',
  '.v-btn--loading',
  '.v-data-table__progress',
  '[data-loading="true"]',
];

// data fields pages use for "still fetching": loading, isLoading, isReloading, loadingOrders...
export const LOADING_FIELD = /^(loading|isLoading|reloading|isReloading)([A-Z].*)?$/;
// messages pages show while they fetch
export const LOADING_TEXT = /please wait|loading(\.\.\.|…)|^\s*loading\s*$/i;
// parts of the app that are not the page: the recorder itself and notifications
const IGNORED_COMPONENTS = ['MacroRecorderBar', 'SnackbarNotification'];
const IGNORED_TEXT_SELECTOR = '.macro-recorder-bar, .v-snack, script, style';

const activity = {
  inFlight: 0,
  lastChange: Date.now(),
  tracking: false,
};

function noteChange(delta) {
  activity.inFlight = Math.max(0, activity.inFlight + delta);
  activity.lastChange = Date.now();
}

/**
 * Count requests through the app's axios instance. Safe to call more than once and
 * with a mocked api that has no interceptors.
 */
export function trackApiActivity(api) {
  if (activity.tracking || !api || !api.interceptors) {
    return false;
  }
  activity.tracking = true;
  api.interceptors.request.use((config) => {
    noteChange(1);
    return config;
  }, (error) => {
    noteChange(0);
    return Promise.reject(error);
  });
  api.interceptors.response.use((response) => {
    noteChange(-1);
    return response;
  }, (error) => {
    noteChange(-1);
    return Promise.reject(error);
  });
  return true;
}

export function getApiActivity() {
  return {...activity};
}

export function hasLoadingIndicators(root = document) {
  return [...root.querySelectorAll(LOADING_SELECTORS.join(', '))]
    .some(element => !element.closest('.macro-recorder-bar'));
}

/** a loading flag is on when it is true or a count above zero; strings, promises and objects are not flags */
function isFlagOn(value) {
  return value === true || (typeof value === 'number' && value > 0);
}

/**
 * Walk the mounted component tree and return 'Component.field' for every loading
 * flag that is on. Pass the root vue instance (document.getElementById('app').__vue__).
 */
export function loadingComponentFlags(rootVm) {
  const found = [];
  const visit = (vm, depth) => {
    if (!vm || depth > 60) {
      return;
    }
    const name = (vm.$options && vm.$options.name) || 'anonymous';
    // vuetify's own components (v-img has an isLoading that stays on for a missing image) are not page state
    const isVuetify = /^v-/i.test(name);
    if (!isVuetify && !IGNORED_COMPONENTS.includes(name)) {
      const data = vm.$data || {};
      Object.keys(data).forEach((key) => {
        if (LOADING_FIELD.test(key) && isFlagOn(data[key])) {
          found.push(`${name}.${key}`);
        }
      });
    }
    (vm.$children || []).forEach(child => visit(child, depth + 1));
  };
  visit(rootVm, 0);
  return found;
}

function isElementShown(element) {
  if (element.hidden || element.closest(IGNORED_TEXT_SELECTOR)) {
    return false;
  }
  const view = element.ownerDocument.defaultView;
  return !view || view.getComputedStyle(element).display !== 'none';
}

/**
 * Return the visible bits of text on the page that read as a loading message.
 */
export function loadingMessages(root = document) {
  const body = root.body || root;
  if (!body || !root.createTreeWalker) {
    return [];
  }
  const messages = [];
  const walker = root.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent;
    if (LOADING_TEXT.test(text) && node.parentElement && isElementShown(node.parentElement)) {
      messages.push(text.trim().replace(/\s+/g, ' ').slice(0, 80));
    }
  }
  return messages;
}

function rootVueInstance(root) {
  const app = root.getElementById && root.getElementById('app');
  return app && app.__vue__;
}

/**
 * Everything that says the page is still loading, as short strings; empty when settled.
 */
export function busyReasons({root = document, rootVm = rootVueInstance(root)} = {}) {
  const reasons = [];
  if (activity.inFlight > 0) {
    reasons.push(`${activity.inFlight} api request${activity.inFlight === 1 ? '' : 's'} in flight`);
  }
  loadingComponentFlags(rootVm).forEach(flag => reasons.push(flag));
  if (hasLoadingIndicators(root)) {
    reasons.push('loading indicator on page');
  }
  loadingMessages(root).forEach(message => reasons.push(`"${message}"`));
  return reasons;
}

export function isPageBusy(options) {
  return busyReasons(options).length > 0;
}

const defaultSleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Resolve once the page has been idle for quietMs. A page that never settles
 * resolves after timeoutMs with settled=false (and why) so a replay can carry on
 * and say so.
 *
 * @returns {Promise<{settled: boolean, waitedMs: number, reasons: string[]}>}
 */
export async function waitForPageSettled({
                                           quietMs = 2000,
                                           timeoutMs = 90000,
                                           pollMs = 250,
                                           getBusyReasons = busyReasons,
                                           sleep = defaultSleep,
                                           now = Date.now,
                                         } = {}) {
  const startedAt = now();
  let quietSince = null;
  let reasons = [];
  for (; ;) {
    const t = now();
    reasons = getBusyReasons();
    if (reasons.length > 0) {
      quietSince = null;
    } else if (quietSince === null) {
      quietSince = t;
    } else if (t - quietSince >= quietMs) {
      return {settled: true, waitedMs: t - startedAt, reasons: []};
    }
    if (t - startedAt >= timeoutMs) {
      console.warn('page did not settle', reasons);
      return {settled: false, waitedMs: t - startedAt, reasons};
    }
    await sleep(pollMs);
  }
}
